import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { SortType } from "../../../../../../models/SortType";
import {
  OpsTatDistribution,
  OpsTatDistributionBucket,
  OpsTatStat,
  OpsTatTableColumn,
} from "../../../../../../models/Dashboard/OpsTat/OpsTatDrilldown";
import {
  OpsTatRecordScope,
  OpsTatSortDir,
  OpsTatStatus,
  resolveOpsInwardDevPageSize,
} from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import { NewOperationsDashboardStore } from "../../../../stores/dashboards/NewOperationsDashboardStore";
import { trackOpsTatAction } from "../../../../../../analytics/opsTatTracking";
import { formatOpsTatStatValue, getOpsTatStatusColors } from "./opsTatFormat";
import OpsTatDrilldownTable from "./OpsTatDrilldownTable";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
} from "../../../../../../styles/modernListingPageSx";

const DRAWER_Z_OFFSET = 2000;
const MAX_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [100, 50, 25];
const INWARD_DEV_PAGE_SIZE_OPTIONS = [100, 50];
const INWARD_DEV_VENDOR_PAGE_SIZE_OPTIONS = [10, 50, 100];

const SCOPE_OPTIONS: Array<{ id: OpsTatRecordScope; label: string }> = [
  { id: "measured", label: "Measured" },
  { id: "open", label: "Open" },
  { id: "all", label: "All" },
];

const ORDER_TO_PR_SORT = ["tat_days", "pr_raised", "project_created"];
const STAGE_02_07_SORT = ["actual", "over", "entered_at", "exited_at"];
const CYCLE_SORT = ["cycle_days", "over", "dispatched", "created", "order_id"];
const STAGE_02_07_IDS = new Set([
  "pr_to_po",
  "po_to_sent",
  "po_to_raw_in",
  "raw_in_to_raw_qc",
  "raw_qc_to_print_out",
  "print_out_to_print_in",
  "print_in_to_print_qc",
]);

const HEADLINE_METRIC_IDS = new Set(["cycle_total", "sla_adherence"]);
const HEADLINE_HIDDEN_COLUMN_KEYS = new Set([
  "slowest_stage",
  "stage_breakdown",
]);

const SORT_WHITELIST: Record<string, string[]> = {
  order_to_pr: ORDER_TO_PR_SORT,
  custom_mix: [
    "created",
    "order_id",
    "path",
    "dispatched",
    "cycle_days",
    "over",
  ],
  cycle_total: CYCLE_SORT,
  sla_adherence: CYCLE_SORT,
  dispatch_deviation: [
    "deviation_days",
    "dispatched",
    "ready_at",
    "created",
    "order_id",
  ],
  raw_inward_dev: [
    "deviation_days",
    "promised_date",
    "actual_date",
    "order_id",
    "exp_days",
  ],
  print_inward_dev: [
    "deviation_days",
    "promised_date",
    "actual_date",
    "order_id",
    "exp_days",
  ],
  raw_qc_dev: [
    "deviation_days",
    "promised_date",
    "actual_date",
    "order_id",
    "exp_days",
  ],
  print_qc_dev: [
    "deviation_days",
    "promised_date",
    "actual_date",
    "order_id",
    "exp_days",
  ],
  bias: [
    "deviation_days",
    "promised_date",
    "actual_date",
    "order_id",
    "exp_days",
  ],
  suggested_use: [
    "deviation_days",
    "promised_date",
    "actual_date",
    "order_id",
    "exp_days",
  ],
  open_overdue: ["deviation_days", "promised_date", "order_id"],
  fill_rate: ["order_id", "product"],
  vendor_dev: [
    "raw_dev_p50",
    "print_dev_p50",
    "on_time_pct",
    "lines",
    "vendor",
  ],
};

const INWARD_DEV_SCOPED_METRICS = new Set([
  "raw_inward_dev",
  "print_inward_dev",
  "raw_qc_dev",
  "print_qc_dev",
]);

interface StatusTone {
  fg: string;
  bg: string;
}

interface OpsTatDrilldownDrawerProps {
  store: NewOperationsDashboardStore;
}

const isPerHundredMetricLabel = (value: string): boolean => {
  try {
    const normalized = value.trim().toLowerCase().replace(/[_-]+/g, " ");
    return (
      /per\s*100/.test(normalized) ||
      /per unit/.test(normalized) ||
      /days per basis/.test(normalized)
    );
  } catch (error) {
    return false;
  }
};

const getVisibleTableColumns = (
  metricId: string,
  columns: OpsTatTableColumn[],
): OpsTatTableColumn[] => {
  try {
    const withoutPerHundred = columns.filter(
      (column) =>
        !isPerHundredMetricLabel(column.key) &&
        !isPerHundredMetricLabel(column.label),
    );
    if (!HEADLINE_METRIC_IDS.has(metricId)) {
      return withoutPerHundred;
    }
    return withoutPerHundred.filter(
      (column) => !HEADLINE_HIDDEN_COLUMN_KEYS.has(column.key),
    );
  } catch (error) {
    console.error(error);
    return columns;
  }
};

const getVisibleStats = (stats: OpsTatStat[]): OpsTatStat[] => {
  try {
    return stats.filter((stat) => !isPerHundredMetricLabel(stat.label));
  } catch (error) {
    return stats;
  }
};

const getSortWhitelist = (
  metricId: string,
  columns: OpsTatTableColumn[],
): string[] => {
  try {
    if (SORT_WHITELIST[metricId]) {
      return SORT_WHITELIST[metricId];
    }
    if (STAGE_02_07_IDS.has(metricId)) {
      return STAGE_02_07_SORT;
    }
    if (metricId === "top_drag") {
      const keys = new Set(columns.map((column) => column.key));
      return keys.has("tat_days") ? ORDER_TO_PR_SORT : STAGE_02_07_SORT;
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

const formatStatValue = (stat: OpsTatStat): string => {
  try {
    return formatOpsTatStatValue(stat.value, stat.unit ?? "", stat.label);
  } catch (error) {
    console.error(error);
    return "—";
  }
};

const getVisibleRowRange = (
  page: number,
  pageSize: number,
  total: number,
  rowCount: number,
): { from: number; to: number } => {
  try {
    if (total <= 0 || rowCount <= 0) {
      return { from: 0, to: 0 };
    }
    const from = (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + 1;
    return { from, to: from + rowCount - 1 };
  } catch (error) {
    console.error(error);
    return { from: 0, to: 0 };
  }
};

const getCyclesCount = (stats: OpsTatStat[]): number | null => {
  try {
    const cycles = stats.find(
      (stat) => stat.label.trim().toLowerCase() === "cycles",
    );
    if (cycles?.value == null || !Number.isFinite(cycles.value)) {
      return null;
    }
    return cycles.value;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getStatValueByLabel = (
  stats: OpsTatStat[],
  label: string,
): number | null => {
  try {
    const match = stats.find(
      (stat) => stat.label.trim().toLowerCase() === label.trim().toLowerCase(),
    );
    if (match?.value == null || !Number.isFinite(match.value)) {
      return null;
    }
    return match.value;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getStatusTone = (
  theme: Theme,
  status: OpsTatStatus | "clay" | "amber",
): StatusTone => getOpsTatStatusColors(theme, status);

const formatBucketLabel = (bucket: OpsTatDistributionBucket): string => {
  try {
    if (bucket.label) {
      return bucket.label;
    }
    if (bucket.hi == null) {
      return `${bucket.lo}+`;
    }
    return `${bucket.lo}–${bucket.hi}`;
  } catch (error) {
    console.error(error);
    return "—";
  }
};

const getHistogramMarkerX = (
  value: number | null,
  buckets: OpsTatDistributionBucket[],
  barX: (index: number) => number,
  barWidth: number,
): number | null => {
  try {
    if (value == null || !Number.isFinite(value) || buckets.length === 0) {
      return null;
    }

    for (let index = 0; index < buckets.length; index += 1) {
      const { lo, hi } = buckets[index];
      const start = barX(index);
      if (hi == null) {
        if (value >= lo) {
          return start + barWidth * 0.2;
        }
        continue;
      }
      if (value >= lo && value < hi) {
        const span = hi - lo || 1;
        return start + ((value - lo) / span) * barWidth;
      }
      if (index === buckets.length - 1 && value === hi) {
        return start + barWidth;
      }
    }

    if (value < buckets[0].lo) {
      return barX(0);
    }
    const lastIndex = buckets.length - 1;
    return barX(lastIndex) + barWidth;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const OpsTatHistogram: React.FC<{
  distribution: OpsTatDistribution;
  mean?: number | null;
}> = ({ distribution, mean }) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;
  const { buckets, p50, p90 } = distribution;
  const meanValue =
    mean != null && Number.isFinite(mean)
      ? mean
      : distribution.mean != null && Number.isFinite(distribution.mean)
        ? distribution.mean
        : null;

  if (!buckets.length) {
    return null;
  }

  const width = 720;
  const height = 192;
  const pad = { top: 48, right: 10, bottom: 28, left: 10 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const gap = 4;
  const barWidth = Math.max(
    (innerWidth - gap * Math.max(buckets.length - 1, 0)) / buckets.length,
    2,
  );
  const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);
  const barX = (index: number) => pad.left + index * (barWidth + gap);
  const p50X = getHistogramMarkerX(p50, buckets, barX, barWidth);
  const p90X = getHistogramMarkerX(p90, buckets, barX, barWidth);
  const meanX = getHistogramMarkerX(meanValue, buckets, barX, barWidth);
  const countFontSize = buckets.length > 10 ? 8 : 10;
  const meanColor = theme.palette.goldenYellow.main;
  const p50Color = theme.palette.orange.main;
  const p90Color = customColors.errorRed;
  const legendY = 16;
  const meanLabelY =
    meanX != null && p50X != null && Math.abs(meanX - p50X) < 36 ? 32 : legendY;

  return (
    <Box>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label="TAT distribution histogram"
      >
        {buckets.map((bucket, index) => {
          const countHeight =
            bucket.count > 0 ? (bucket.count / maxCount) * innerHeight : 0;
          const x = barX(index);
          const y = pad.top + innerHeight - countHeight;
          const isOverflow = bucket.hi == null;
          const countLabelY = Math.max(y - 4, pad.top - 8);
          return (
            <g key={`${bucket.lo}-${bucket.hi ?? "overflow"}-${index}`}>
              <title>{`${formatBucketLabel(bucket)}: ${bucket.count}`}</title>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={countHeight}
                rx={2}
                fill={
                  isOverflow
                    ? customColors.greyTextColor
                    : customColors.darkBlue
                }
                opacity={isOverflow ? 0.55 : 0.85}
              />
              <text
                x={x + barWidth / 2}
                y={countLabelY}
                textAnchor="middle"
                fill={customColors.navyText}
                fontSize={countFontSize}
                fontWeight={600}
              >
                {bucket.count}
              </text>
              <text
                x={x + barWidth / 2}
                y={height - 8}
                textAnchor="middle"
                fill={customColors.greyTextColor}
                fontSize={buckets.length > 10 ? 8 : 10}
              >
                {formatBucketLabel(bucket)}
              </text>
            </g>
          );
        })}
        {meanX != null ? (
          <g>
            <line
              x1={meanX}
              x2={meanX}
              y1={meanLabelY + 2}
              y2={pad.top + innerHeight}
              stroke={meanColor}
              strokeWidth={1.5}
              strokeDasharray="2 4"
            />
            <text
              x={meanX + 4}
              y={meanLabelY}
              fill={meanColor}
              fontSize={10}
              fontWeight={700}
            >
              Mean
            </text>
          </g>
        ) : null}
        {p50X != null ? (
          <g>
            <line
              x1={p50X}
              x2={p50X}
              y1={legendY + 2}
              y2={pad.top + innerHeight}
              stroke={p50Color}
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
            <text
              x={p50X + 4}
              y={legendY}
              fill={p50Color}
              fontSize={10}
              fontWeight={700}
            >
              P50
            </text>
          </g>
        ) : null}
        {p90X != null ? (
          <g>
            <line
              x1={p90X}
              x2={p90X}
              y1={legendY + 2}
              y2={pad.top + innerHeight}
              stroke={p90Color}
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
            <text
              x={p90X + 4}
              y={legendY}
              fill={p90Color}
              fontSize={10}
              fontWeight={700}
            >
              P90
            </text>
          </g>
        ) : null}
      </svg>
    </Box>
  );
};

const OpsTatDrilldownDrawer: React.FC<OpsTatDrilldownDrawerProps> = ({
  store,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const [formulaOpen, setFormulaOpen] = useState(true);

  const open = store.drawerOpen;
  const drilldown = store.drilldownResult;
  const data = drilldown?.data ?? null;
  const query = store.drilldownQuery;
  const isInwardDev = store.drawerSource === "inward_dev";
  const metricId = data?.metricId || store.drawerMetricId || "";
  const columns = getVisibleTableColumns(metricId, data?.table.columns ?? []);
  const rows = data?.table.rows ?? [];
  const sortableColumns = getSortWhitelist(metricId, columns);
  const activeSort = data?.table.sort ?? query.sort;
  const page = data?.table.page ?? query.page;
  const pageSize = isInwardDev
    ? resolveOpsInwardDevPageSize(query.page_size, metricId)
    : query.page_size || data?.table.page_size || 100;
  const total = data?.table.total ?? 0;
  const visibleRange = getVisibleRowRange(page, pageSize, total, rows.length);
  const cyclesCount = data ? getCyclesCount(data.stats) : null;
  const showScopeToggle =
    !isInwardDev || INWARD_DEV_SCOPED_METRICS.has(metricId);

  useEffect(() => {
    try {
      if (open) {
        setFormulaOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [open, store.drawerMetricId]);

  const handleClose = () => {
    try {
      store.closeDrawer();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRefresh = async () => {
    try {
      await store.refreshData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleScopeChange = async (scope: OpsTatRecordScope) => {
    try {
      if (scope === query.record_scope) {
        return;
      }
      await store.fetchDrilldown({ record_scope: scope, page: 1 });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSortChange = async (column: string, sortType: SortType) => {
    try {
      if (!sortableColumns.includes(column)) {
        return;
      }
      const dir: OpsTatSortDir = sortType === SortType.ASC ? "asc" : "desc";
      await store.fetchDrilldown({
        sort: { column, dir },
        page: 1,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handlePageChange = async (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    nextPage: number,
  ) => {
    try {
      await store.fetchDrilldown({ page: nextPage });
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowsPerPageChange = async (limit: number) => {
    try {
      const requested = Math.min(Math.max(limit, 1), MAX_PAGE_SIZE);
      const pageSizeNext = isInwardDev
        ? resolveOpsInwardDevPageSize(requested, metricId)
        : requested;
      await store.fetchDrilldown({ page_size: pageSizeNext, page: 1 });
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportCsv = async () => {
    try {
      await store.exportCsv();
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleFormula = () => {
    try {
      setFormulaOpen((current) => {
        const next = !current;
        trackOpsTatAction("formula_toggle", store.appliedFilters, {
          expanded: next,
          metricId: store.drawerMetricId || undefined,
          drawerSource: store.drawerSource,
          cardTitle: store.drawerCardTitle || undefined,
          cardSection: store.drawerCardSection || undefined,
          context: {
            formula_expanded: next,
            metric_id: store.drawerMetricId || undefined,
            drawer_source: store.drawerSource,
            ...(store.drawerCardTitle
              ? { card_title: store.drawerCardTitle }
              : {}),
            ...(store.drawerCardSection
              ? { card_section: store.drawerCardSection }
              : {}),
          },
        });
        return next;
      });
    } catch (error) {
      console.error(error);
    }
  };

  const kicker = data?.kicker || "TAT drilldown";
  const title = data?.title || store.drawerMetricId || "Metric";
  const desc = data?.desc || "";
  const showHistogram = Boolean(data?.distribution?.buckets?.length);
  const isPending = data != null && data.ready === false;
  const showEmptyRows =
    data != null &&
    data.ready === true &&
    rows.length === 0 &&
    !store.drilldownLoading;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 880, md: 960 },
          maxWidth: "100vw",
          height: "100%",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="flex-start" minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: customColors.lightBlue,
              color: customColors.darkBlue,
            }}
          >
            <InsightsOutlinedIcon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <CustomTypography
              variant="caption"
              sx={{
                color: colors.subtitle,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {kicker}
            </CustomTypography>
            <CustomTypography
              variant="heading5"
              sx={{ color: colors.title, wordBreak: "break-word" }}
            >
              {title}
            </CustomTypography>
            {desc ? (
              <CustomTypography
                variant="body4"
                sx={{
                  mt: 0.5,
                  color: colors.subtitle,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                {desc}
              </CustomTypography>
            ) : null}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
          <IconButton
            onClick={handleRefresh}
            size="small"
            aria-label="Refresh"
            title="Refresh with applied filters"
            disabled={store.drilldownLoading || store.summaryLoading}
          >
            {store.drilldownLoading ? (
              <CircularProgress size={18} />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
          <IconButton onClick={handleClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          ...modernDrawerBodySx(),
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {store.drilldownError ? (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.infoBorder}`,
            }}
          >
            {store.drilldownError}
          </Alert>
        ) : null}
        {store.exportError ? (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.infoBorder}`,
            }}
          >
            {store.exportError}
          </Alert>
        ) : null}

        {store.drilldownLoading && !data ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {data ? (
          <>
            <Grid2 container spacing={1.5}>
              {getVisibleStats(data.stats).map((stat) => {
                const highlightColor =
                  stat.color === "clay" || stat.color === "amber"
                    ? stat.color
                    : null;
                const tone = highlightColor
                  ? getStatusTone(theme, highlightColor)
                  : {
                      fg: customColors.navyText,
                      bg: customColors.white,
                    };
                return (
                  <Grid2 key={stat.label} size={{ xs: 6, sm: 4, md: 2 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        height: "100%",
                        borderRadius: 2.5,
                        bgcolor: highlightColor ? tone.bg : customColors.white,
                        border: `1px solid ${customColors.softCardBorder}`,
                        borderLeft: `4px solid ${highlightColor ? tone.fg : customColors.darkBlue}`,
                        boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                      }}
                    >
                      <CustomTypography
                        variant="body4"
                        sx={{
                          color: highlightColor
                            ? tone.fg
                            : customColors.mutedSlateText,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                        }}
                      >
                        {stat.label}
                      </CustomTypography>
                      <CustomTypography
                        variant="heading5"
                        sx={{
                          mt: 0.5,
                          color: highlightColor
                            ? tone.fg
                            : customColors.navyText,
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          wordBreak: "break-word",
                          hyphens: "none",
                        }}
                      >
                        {formatStatValue(stat)}
                      </CustomTypography>
                    </Paper>
                  </Grid2>
                );
              })}
            </Grid2>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                overflow: "hidden",
                boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
              }}
            >
              <Box
                onClick={handleToggleFormula}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  bgcolor: customColors.white,
                  "&:hover": { bgcolor: customColors.hoverGray },
                }}
              >
                <CustomTypography
                  variant="heading7"
                  sx={{ color: colors.title }}
                >
                  How this is calculated
                </CustomTypography>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleFormula();
                  }}
                  aria-label={
                    formulaOpen ? "Collapse formula" : "Expand formula"
                  }
                >
                  {formulaOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={formulaOpen} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      bgcolor: customColors.veryLightGray,
                      border: `1px solid ${customColors.lightGray}`,
                      overflowX: "auto",
                    }}
                  >
                    <CustomTypography
                      variant="body4"
                      sx={{
                        color: customColors.navyText,
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      {data.formula || "—"}
                    </CustomTypography>
                  </Box>
                  {data.lineage.length > 0 ? (
                    <Box sx={{ mt: 1.5 }}>
                      <CustomTypography
                        variant="body4"
                        sx={{
                          mb: 1,
                          color: customColors.mutedSlateText,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Source
                      </CustomTypography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          alignItems: "center",
                        }}
                      >
                        {data.lineage.map((source, index) => (
                          <React.Fragment key={`${source}-${index}`}>
                            {index > 0 ? (
                              <CustomTypography
                                variant="body4"
                                sx={{ color: customColors.greyTextColor }}
                              >
                                →
                              </CustomTypography>
                            ) : null}
                            <Chip
                              size="small"
                              label={source}
                              sx={{
                                height: 22,
                                fontSize: "0.68rem",
                                fontFamily: "monospace",
                                bgcolor: customColors.white,
                                color: customColors.navyText,
                                border: `1px solid ${customColors.lightGray}`,
                              }}
                            />
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Collapse>
            </Paper>

            {showHistogram && data.distribution ? (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${colors.border}`,
                  bgcolor: customColors.white,
                  boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                }}
              >
                <CustomTypography
                  variant="heading7"
                  sx={{ color: colors.title, mb: 1 }}
                >
                  Distribution
                </CustomTypography>
                <OpsTatHistogram
                  distribution={data.distribution}
                  mean={
                    data.distribution.mean ??
                    getStatValueByLabel(data.stats, "mean")
                  }
                />
              </Paper>
            ) : null}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <CustomTypography
                  variant="heading7"
                  sx={{ color: colors.title }}
                >
                  Records
                </CustomTypography>
                {showScopeToggle
                  ? SCOPE_OPTIONS.map((option) => (
                      <AppButton
                        key={option.id}
                        variant={
                          query.record_scope === option.id
                            ? "primary"
                            : "transparentPrimary"
                        }
                        label={option.label}
                        onClick={() => {
                          void handleScopeChange(option.id);
                        }}
                        disabled={store.drilldownLoading}
                        customStyles={{
                          minHeight: 32,
                          px: 1.5,
                        }}
                        fontSize="0.75rem"
                      />
                    ))
                  : null}
              </Box>
              <AppButton
                variant="primary"
                label={store.exportLoading ? "Exporting..." : "Export CSV"}
                startIcon={<DownloadIcon />}
                onClick={() => {
                  void handleExportCsv();
                }}
                disabled={store.exportLoading || !store.drawerMetricId}
                customStyles={{ minHeight: 32 }}
                fontSize="0.75rem"
              />
            </Box>

            <CustomTypography
              variant="body4"
              sx={{
                color: customColors.mutedSlateText,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {total <= 0
                ? "Showing 0 of 0"
                : `Showing ${visibleRange.from}–${visibleRange.to} of ${total}`}
              {cyclesCount != null && query.record_scope !== "measured"
                ? ` · Cycles ${formatOpsTatStatValue(cyclesCount, "", "Cycles")} measured`
                : null}
            </CustomTypography>

            {data.note ? (
              <CustomTypography
                variant="body4"
                sx={{ color: customColors.mutedSlateText }}
              >
                {!isInwardDev && query.record_scope !== "measured"
                  ? `Stats stay on the measured set. ${data.note}`
                  : data.note}
              </CustomTypography>
            ) : !isInwardDev && query.record_scope !== "measured" ? (
              <CustomTypography
                variant="body4"
                sx={{ color: customColors.mutedSlateText }}
              >
                Stats stay on the measured set when viewing open or all rows.
              </CustomTypography>
            ) : null}

            {isPending ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="8rem"
                textAlign="center"
                border="1px dashed"
                borderColor="divider"
                borderRadius={2}
                px={2}
                py={2}
                gap={1}
              >
                <CustomTypography
                  variant="heading6"
                  fontWeight={500}
                  color={customColors.greyTextColor}
                >
                  This metric is pending.
                </CustomTypography>
                {data.note ? (
                  <CustomTypography
                    variant="body4"
                    color={customColors.greyTextColor}
                  >
                    {data.note}
                  </CustomTypography>
                ) : null}
              </Box>
            ) : null}

            {showEmptyRows ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10rem"
                textAlign="center"
                border="1px dashed"
                borderColor="divider"
                borderRadius={2}
              >
                <CustomTypography
                  variant="heading6"
                  fontWeight={500}
                  color={customColors.greyTextColor}
                >
                  No records for this scope.
                </CustomTypography>
              </Box>
            ) : rows.length > 0 && !isPending ? (
              <Box sx={{ position: "relative" }}>
                {store.drilldownLoading ? (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${customColors.white}99`,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : null}
                <OpsTatDrilldownTable
                  maxHeight={window.innerHeight - 350}
                  columns={columns}
                  rows={rows}
                  page_no={page}
                  limit={pageSize}
                  total_count={total}
                  sortableColumns={sortableColumns}
                  sortBy={activeSort?.column}
                  sortType={
                    activeSort?.dir === "asc" ? SortType.ASC : SortType.DESC
                  }
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={handleSortChange}
                  rowsPerPageOptions={
                    isInwardDev
                      ? metricId === "vendor_dev"
                        ? INWARD_DEV_VENDOR_PAGE_SIZE_OPTIONS
                        : INWARD_DEV_PAGE_SIZE_OPTIONS
                      : PAGE_SIZE_OPTIONS
                  }
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default observer(OpsTatDrilldownDrawer);
